import type { Member } from './constants';

const ACCOUNT_MANAGEMENT_LE_ADMIN = 'account_management/legal_entity:admin';
const WORKSPACE_ORG_ADMIN = 'workspace_org_admin';

// The workspace members API always returns the current user first.
export function getCurrentUser(memberList: Member[]): Member | undefined {
  return memberList[0];
}

/**
 * Returns true if the current user may manage members (invite, edit roles).
 * Requires either:
 * - account_management/legal_entity:admin on at least one legal entity, or
 * - workspace_org_admin (org-level)
 *
 * `currentUserOrgRoles` must be the `roles` field from the current user's entry
 * in GET /organization/v1/workspace/members (members[0].roles). This field contains
 * org-level roles like `workspace_org_admin`, which is NOT present in the
 * admin-entities response's org-level roles.
 */
export function canManageMembers(memberList: Member[], currentUserOrgRoles?: string[]): boolean {
  const currentUser = getCurrentUser(memberList);
  return (
    (currentUser?.roles?.includes(ACCOUNT_MANAGEMENT_LE_ADMIN) ?? false) ||
    (currentUserOrgRoles?.includes(WORKSPACE_ORG_ADMIN) ?? false)
  );
}

/**
 * Returns true if the current user may remove a member from the organization.
 * Requires the workspace_org_admin role.
 *
 * `currentUserOrgRoles` must be the `roles` field from the current user's entry
 * in GET /organization/v1/workspace/members (members[0].roles). This field contains
 * org-level roles like `workspace_org_admin`, which is distinct from legalEntityRoles
 * and is NOT present in the admin-entities response's org-level roles.
 */
export function canRemoveMembers(currentUserOrgRoles: string[] | undefined): boolean {
  return currentUserOrgRoles?.includes(WORKSPACE_ORG_ADMIN) ?? false;
}
